import type { Dictionary } from "./types";

const en: Dictionary = {
  appName: "LK Storekeeper",
  login: {
    subtitle: "Sign in with your admin or storekeeper account.",
    username: "Username",
    password: "Password",
    signIn: "Sign in",
    signingIn: "Signing in...",
    loginFailed: "Login failed",
  },
  header: {
    logout: "Log out",
    language: "Language",
    theme: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    session: "Session",
    sessionAccess: "Access token",
    sessionRefresh: "Sign-in valid",
    sessionExpired: "expired",
    networkDebug: "Network debug",
    otherStatuses: "Other statuses",
    shortReceiptNumbers: "FO No. without /N",
    allPages: "All pages",
  },
  online: {
    label: "Who's online",
    online: "Online",
    offline: "Offline",
    active: "Active now",
    idle: "Tab in background",
    empty: "Nobody is online right now",
    you: "you",
    never: "never signed in",
    loading: "Loading…",
  },
  tabs: {
    ui: "UI",
    db: "DB",
  },
  tablesIndex: {
    heading: "Tables",
    editable: "Editable",
    columns: (count) => `${count} columns`,
    notFound: "Table not found in database",
  },
  tableView: {
    addRow: "Add row",
    edit: "Edit",
    noRows: "No rows yet.",
    pageInfo: (page, totalPages, total) => `Page ${page} of ${totalPages} (${total} rows)`,
    previous: "Previous",
    next: "Next",
    addTitle: "Add row",
    editTitle: "Edit row",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving...",
    loading: "Loading...",
    selectPlaceholder: "— select —",
    loadingOptions: "Loading options...",
    optionsLoadFailed: "Failed to load options",
    searchPlaceholder: "Search...",
    noSearchResults: "No matches found",
    searchTable: "Search...",
  },
  errors: {
    loadFailed: "Failed to load table",
    saveFailed: "Save failed",
    deleteFailed: "Delete failed",
    serverError: "Something went wrong on the server. Try again; if it keeps happening, contact an administrator.",
    backendUnreachable: "The warehouse server is unreachable. Check your connection and try again later.",
    duplicate: "A record with the same values already exists.",
    stillReferenced: "This record is used by other data and can't be removed or changed.",
    missingReference: "A linked record wasn't found — it may have been deleted.",
    requiredField: "A required field is empty.",
    integrityViolation: "This change conflicts with existing data.",
  },
  units: {
    kg: "kg",
    m3: "m³",
    kgM3: "kg/m³",
  },
  confirm: {
    yes: "Yes",
    no: "No",
    confirm: "Confirm",
    cancel: "Cancel",
    close: "Close",
    discardTitle: "Discard changes?",
    discardMessage:
      "This form has not been submitted. If you close it now, the entered data will be lost.",
    discardConfirm: "Discard",
    keepEditing: "Keep editing",
  },
  workflow: {
    receiptPicker: {
      heading: "Forwarding order",
      placeholder: "— select a forwarding order —",
      newButton: "New forwarding order",
      newTitle: "New forwarding order",
      orderNumber: "Order number",
      cargoName: "Cargo name",
      warehouse: "Warehouse",
      isOurFirstMile: "Our first mile",
      plannedWeightKg: "Planned weight (kg)",
      plannedVolumeM3: "Planned volume (m³)",
      plannedFigure: "Planned (from the sender)",
      actualFigure: "Current remainder (received minus shipped/written off)",
      orderNumberHint: (max) => `At most ${max} characters.`,
      create: "Create",
      creating: "Creating...",
      noReceipts: "No forwarding orders yet.",
      editButton: "Edit forwarding order",
      editTitle: "Edit forwarding order",
      sequenceNumber: "FO no. within order",
      renumberWarning: (from, to) =>
        `${from} will become ${to} everywhere, including package type codes and operation history. Labels printed earlier will no longer match.`,
      save: "Save",
      saving: "Saving...",
      saved: "Changes saved.",
      cargoNameOriginal: "Cargo name (as supplied)",
      cargoNameRu: "Cargo name (Russian)",
      cargoNameEn: "Cargo name (English)",
      cargoNameZh: "Cargo name (Chinese)",
      cargoNameTranslations: "Cargo name translations",
      totalPackageCount: "Total packages",
      cargoReadyOn: "Cargo ready on",
      selectedLabel: (receiptNumber) => `FO ${receiptNumber}`,
      scanButton: "Scan order QR code",
      scanTitle: "Scan order QR code",
      scanHint: "Point the camera at the order's QR code.",
      scanLoading: "Starting camera...",
      scanCameraError: "Couldn't access the camera. Check permissions and try again.",
      scanMatchesTitle: "Select a forwarding order",
      scanNotFound: (orderNumber) => `No forwarding order found for order ${orderNumber}.`,
      shareButton: "Share forwarding order",
      shareTitle: "Share forwarding order",
      shareCopyReceipt: "Copy forwarding order number",
      shareCopyLink: "Copy link",
      shareCopied: "Copied!",
      shareQrHint: "Scan to open this forwarding order.",
      supplier: "Sender",
      supplierPlaceholder: "— select sender —",
      supplierSearchPlaceholder: "Search sender...",
      supplierNoResults: "No senders found.",
      supplierNone: "No sender",
      supplierAddAnother: "Add sender",
      supplierRemove: "Remove sender",
      trackingNumbers: "Tracking number(s)",
      warehouseArrivalOn: "Arrival date",
      createdBy: "Created by",
      statusLabel: "Status",
      commentLabel: "Comment",
      commentPlaceholder: "Add a comment to this forwarding order…",
      statusNames: {
        awaiting_arrival: "Awaiting arrival",
        partially_received: "Partially received",
        received: "Received",
        scheduled_for_trip: "Scheduled for trip",
        partially_shipped: "Partially shipped",
        shipped: "Shipped",
      },
    },
    steps: {
      main: "Main",
      operations: "Warehouse operations",
      packageTypes: "Package types",
      receiving: "Receiving",
      shipping: "Shipping / write-off",
      transfer: "Transfer",
      balances: "Balances",
      connections: "Connections",
      files: "Files",
      chat: "Chat",
      more: "More",
      arrivals: "Expected",
      services: "Extra services",
    },
    main: {
      receiptFields: "Forwarding order details",
      cargoName: "Cargo name",
      plannedWeightKg: "Planned weight (kg)",
      plannedVolumeM3: "Planned volume (m³)",
      actualVolumeM3: "Actual volume (m³)",
      totalPackageCount: "Total places",
      packageTypesHeading: "Package types",
      colDimensions: "Package type",
      colCode: "Code",
      colPackaging: "Packaging",
      colCount: "Places",
      colWeight: "Weight",
      colLocation: "Location",
      colReceivedOn: "Received on",
      printSticker: "Print box sticker",
      openReceiving: "Receiving",
      openShipping: "Write-off",
      noPackageTypes: "This forwarding order has no package types yet.",
      toggleBreakdown: "Show by location",
      warehouseHeading: "Warehouse",
      warehouseDescription: "Receiving, shipping and package types for this forwarding order.",
      colVolume: "Volume",
    },
    receiptHeader: {
      date: "Created",
      manager: "Manager",
      managerPlaceholder: "Not assigned",
      planHeading: "Planned cargo figures",
      acceptedHeading: "In the warehouse",
      packages: "Places",
      weight: "Weight",
      volume: "Volume",
      density: "SF",
      cargoReadyOn: "Cargo ready date",
      receivedOn: "Received at warehouse",
      comment: "Comment",
      commentPlaceholder: "Add a comment to this forwarding order…",
      saveComment: "Save",
      savingComment: "Saving…",
      savedComment: "Saved",
    },
    arrivals: {
      heading: "Expected arrivals",
      tag: "Our first mile",
      empty: "No expected arrivals for this forwarding order.",
      ourFirstMile: "Our first mile",
      supplier: "Sender",
      addressAndContacts: "Address and contacts",
      contacts: "Contacts",
      trackingNumbers: "Tracking number",
      plannedArrival: "Arrival date (plan)",
      plannedPackages: "Planned pkgs",
      plannedWeight: "Planned weight",
      plannedVolume: "Planned volume",
      arrivedOn: "Arrival date (actual)",
      deleteSupplier: "Delete sender",
      deleteTitle: "Delete sender?",
      deleteBody: (supplier) =>
        supplier
          ? `Sender "${supplier}" will be removed from this forwarding order.`
          : "This expected-arrival row without a sender will be deleted.",
      deleteWarning: "Its tracking number and planned arrival are deleted too.",
    },
    services: {
      heading: "Extra services",
      placeholder: "Tracking of extra services is coming later.",
      palletizing: "Palletizing",
      marking: "Labelling",
      soon: "Soon",
    },
    operationsLog: {
      colPackages: "Places",
      colWeight: "Weight",
      colVolume: "Volume",
      heading: "Warehouse operations",
      description: "Every operation on this forwarding order.",
      empty: "No operations on this forwarding order yet.",
      colType: "Type",
      colNumber: "ID",
      colDate: "Date",
      colComment: "Comment",
      typeReceiving: "Receiving",
      typeShipping: "Shipping",
      typeTransfer: "Transfer",
      typeReceiptTransfer: "Carry-over",
      detailTitle: (type, id, date) => `${type} ${id} of ${date}`,
      detailColPackageType: "Package type",
      detailColCount: "Places",
      detailColWeight: "Weight",
      detailColLocation: "Location",
      receiptDocument: (number) => `Receipt ${number}`,
      shippingOrder: (number) => `Shipping order ${number}`,
    },
    boxSticker: {
      title: "Box stickers",
      printOne: "Print",
      printAll: "Print all",
      empty: "No places to label yet — post a receiving operation first.",
      labelReceipt: "Receipt",
      labelOrder: "Order",
      labelCode: "Code",
      labelDimensions: "L*W*H",
      labelPlace: "Place",
      orientation: "Orientation",
      vertical: "Vertical",
      horizontal: "Horizontal",
      generating: "Generating stickers…",
      generatingOne: "Generating…",
      progress: (done, total) => `${done} of ${total}`,
      ready: "Stickers are ready",
      openPdf: "Open PDF",
      failed: "Could not generate the stickers",
    },
    receiptOverview: {
      shownOf: (shown, total) => `Showing ${shown} of ${total}`,
      loadMore: "Load more",
      heading: "Expected arrivals",
      loading: "Loading expected arrivals...",
      empty: "No expected arrivals in the warehouse.",
      receiptNumber: "Receipt №",
      cargo: "Cargo",
      supplier: "Sender",
      supplierAddress: "Address",
      supplierPhone: "Phone",
      supplierEmail: "Email",
      cargoReadyOn: "Ready",
      receivedOn: "Received",
      arrivalOn: "Arrival",
      trackingNumbers: "Tracking",
      trackingContacts: "Tracking contacts",
      contactsDialogTitle: (receipt: string) => `Contacts for ${receipt}`,
      copy: "Copy",
      copied: "Copied",
      packagesShort: "Pkgs",
      weightShort: "Weight",
      volumeShort: "Volume",
      sfShort: "SF",
      originalName: (name) => `Original: ${name}`,
      createdBy: "Created by",
      plannedPackages: "Planned pkgs",
      plannedWeight: "Planned weight",
      plannedVolume: "Planned volume",
      shippedPackages: "Shipped pkgs",
      shippedWeight: "Shipped weight",
      shippedVolume: "Shipped volume",
      planCaption: "planned",
      shippedCaption: "shipped",
      supplierShippedOn: "Shipped on",
      plannedArrivalOn: "Planned arrival",
      arrivalFrom: "Planned arrival from",
      arrivalTo: "to",
      datePlaceholder: "dd.mm.yyyy",
      overdueOnly: "Overdue only",
      resetFilters: "Reset",
      filteredEmpty: "No receipts with a planned arrival date in this period.",
      overdue: "Overdue",
      comment: "Note",
    },
    landing: {
      tripBanner: (count: number) => `${count} ${count === 1 ? "order" : "orders"} to ship`,
      tripBannerAction: "Pick a trip's orders and ship them",
      expectedTab: "Expected",
      inWarehouseTab: "In stock",
      otherTab: "Other statuses",
    },
    inWarehouse: {
      heading: "In the warehouse",
      loading: "Loading warehouse stock...",
      empty: "Nothing is currently in stock.",
      receiptNumber: "Receipt №",
      cargo: "Cargo",
      packages: "Pkgs",
      weight: "Weight",
      volume: "Volume",
      sf: "SF",
      acceptedOn: "Accepted on",
      status: "Cargo status",
      daysInWarehouse: "Days in warehouse",
      comment: "Comment",
      days: (count) => `${count} d`,
    },
    packageTypes: {
      heading: "Package types",
      code: "Code",
      dimensionsLabel: "Dimensions (L*W*H)",
      lengthCm: "Length (cm)",
      widthCm: "Width (cm)",
      heightCm: "Height (cm)",
      add: "Add package type",
      adding: "Adding...",
      addToggle: "Add package type",
      cancel: "Cancel",
      empty: "No package types for this forwarding order yet.",
      selectReceiptFirst: "Select a forwarding order above first.",
      delete: "Delete",
      deleting: "Deleting...",
      deleteConfirmTitle: "Delete package type",
      deleteConfirmMessage: (code) => `Delete package type ${code}?`,
      deleteConfirmWarning: "This cannot be undone.",
    },
    expectedArrivals: {
      heading: "Expected arrivals",
      loading: "Loading expected arrivals...",
      empty: "Select a forwarding order above first.",
      orderNumber: "Order",
      cargoType: "Cargo type",
      itemCount: "Items",
      packageCount: "Packages",
      plannedWeightKg: "Planned weight (kg)",
      plannedVolumeM3: "Planned volume (m³)",
      sf: "SF (kg/m³)",
      trackingNumbers: "Tracking numbers",
      trackingContacts: "Tracking contacts",
      supplierShippedOn: "Shipped on",
      arrivalOn: "Expected arrival",
      cargoReadyOn: "Cargo ready on",
      status: "Status",
      notes: "Notes",
    },
    operationUser: "User",
    operationUserUnknown: "Not recorded",
    conditions: {
      help: "Description",
      emptyCatalog: "The list is empty.",
      handlingTitle: "Handling conditions",
      handlingAdd: "Set handling conditions",
      handlingEdit: "Change handling conditions",
      cargoTitle: "Actual cargo condition at receiving",
      cargoHint: "You can pick several. Hover or tap «?» for the description.",
      cargoEdit: "Change",
      cargoNone: "Not specified",
    },
    lines: {
      heading: "Lines",
      packageType: "Package type",
      packagingType: "Packaging",
      noPackagingTypes: "The packaging types list is empty.",
      location: "Location",
      fromLocation: "From location",
      toLocation: "To location",
      packageCount: "Package count",
      actualWeightKg: "Actual weight (kg)",
      addLine: "Add line",
      removeLine: "Remove",
      removeLineConfirm: "Remove this line? The data entered will be lost.",
      noPackageTypes: "Add a package type first.",
      newPackageTypeOption: "➕ New package type",
      noLocations: "No warehouse locations available.",
      missingPackageType: "Select a package type for every line.",
      missingPackagingType: "Select a packaging type for every line.",
      missingLocation: "Select a warehouse location for every line.",
      missingWeight: "Enter the actual weight for every line.",
      capacityPcs: "Capacity (pcs)",
      capacityValue: (pcs: number) => `Capacity: ${pcs} pcs`,
      missingCapacity: "Enter the capacity (pcs) — a whole number of at least 1 — for every pallet line.",
      locationUnspecified: "Not specified",
    },
    receiving: {
      heading: "Receiving",
      description: "Record incoming packages against a receipt line.",
      modeAssistant: "Receiving assistant",
      modeReceiving: "Receiving",
      modeAssistantHint:
        "Creates package types and posts the receiving: enter the sizes of new packages right here.",
      modeReceivingHint: "Only posts the receiving against existing package types.",
      noPackageTypesForReceiving:
        "This receipt has no package types yet. Create them first via the receiving assistant.",
      operationDate: "Operation date and time",
      comment: "Comment",
      commentPlaceholder: "Optional note about this receiving…",
      newPackageType: "New package type",
      newPackageTypeHint: "Enter L×W×H — the package type is added to this forwarding order.",
      addPackageType: "Add package type",
      addingPackageType: "Adding...",
      photos: "Receiving photos",
      photosHint: "Photos are attached to the receipt and also listed under Documents.",
      photoGuideTitle: "Photo requirements",
      photoGuideItems: [
        {
          term: "Overall view of the batch",
          detail: "everything on the pallet/skid; show the volume and stacking.",
        },
        {
          term: "Each item",
          detail: "2–3 angles; packaging integrity visible.",
        },
        {
          term: "Labelling",
          detail: "close-up; text readable without zooming.",
        },
        {
          term: "Damage",
          detail: "wide, medium and close-up shots (with a ruler).",
        },
        {
          term: "Stacking and securing",
          detail: "front/side/top view; film, straps, dunnage.",
        },
        {
          term: "Documents",
          detail: "details legible; photo of the document next to the cargo.",
        },
      ],
      photoGuideNote: "Important: good lighting, straight-on angle.",
      addPhotos: "Take photos",
      uploadingPhotos: "Uploading...",
      photoCount: (count) => `${count} photo(s)`,
      removePhoto: "Remove photo",
      readOnly: "Cargo receiving cannot be edited.",
      submit: "Post receiving",
      submitting: "Posting...",
      success: "Receiving operation posted.",
      recent: "Recent receiving operations",
      empty: "No receiving operations yet.",
      total: "Total",
      confirmTitle: "Confirm receiving",
      confirmMessage: "Post this receiving operation?",
      arrivalResultQuestion: "The received cargo doesn't match the plan. Which status should the receipt get?",
      arrivalResultArrived: "Arrived",
      arrivalResultPartial: "Partially arrived",
      arrivalResultAuto: "The cargo matches the plan — the receipt will be set to “Arrived”.",
    },
    shipping: {
      heading: "Shipping / write-off",
      description: "Record outgoing or written-off packages.",
      operationDate: "Operation date and time",
      comment: "Comment",
      commentPlaceholder: "Optional note about this shipment…",
      reason: "Shipment type",
      reasonDeparture: "Departure",
      reasonWriteOff: "Write-off",
      submit: "Post shipping",
      submitting: "Posting...",
      success: "Shipping operation posted.",
      recent: "Recent shipping operations",
      empty: "No shipping operations yet.",
      total: "Total",
      confirmTitle: "Confirm shipping",
      confirmMessage: "Post this shipping operation?",
      noStock: "Nothing of this forwarding order is in stock, so there is nothing to write off.",
      notInStock: "A line picks a package type and location with no stock.",
      exceedsPlaces: (code, available) => `${code}: more places than in stock (${available} available).`,
      exceedsWeight: (code, available) => `${code}: more weight than in stock (${available} kg available).`,
      available: (places, weight) => `Available: ${places} places, ${weight} kg`,
      departureHeading: (orderNumber) => `Departure for shipping order ${orderNumber}`,
      writeOffHeading: "Write-off",
      departureDescription:
        "Lines are filled from the stock in each location. Check them: a shipping order is executed only whole.",
      writeOffDescription:
        "Writes cargo off (damaged, lost). Departures go only through a shipping order.",
      loadingDraft: "Filling lines from stock…",
      shortagesTitle: "Not enough stock — the order can't be shipped:",
      shortage: (code, requestedPlaces, availablePlaces, requestedKg, availableKg) =>
        `${code}: needs ${requestedPlaces} places / ${requestedKg} kg, available ${availablePlaces} places / ${availableKg} kg`,
      notWhole: (code, places, kg, gotPlaces, gotKg) =>
        `A shipping order is executed only whole: ${code} needs ${places} places / ${kg} kg, the form has ${gotPlaces} places / ${gotKg} kg.`,
      submitDeparture: "Post departure",
      submitWriteOff: "Post write-off",
      confirmDeparture: "Post the departure? The shipping order will then be executed and can no longer change.",
      confirmWriteOff: "Post the write-off?",
      confirmTitleWriteOff: "Confirm write-off",
      successWriteOff: "Write-off posted.",
    },
    shippingOrders: {
      allOrders: "All orders — ship a trip",
      heading: "Shipping orders",
      description: "Created in 1C when a trip is formed. Cargo leaves only by a shipping order, and only the whole order.",
      empty: "No shipping orders.",
      ship: "Ship",
      executed: (date) => `Executed ${date}`,
      pending: "To ship",
      createdBy: "Created by",
      showStock: "Stock by location",
      noStock: "No stock",
      create: "Create order",
      createTitle: "New shipping order (temporary)",
      createHint: "Temporary button: shipping orders are meant to come from 1C. An order must fit in the free stock.",
      weightKg: "Weight (kg)",
      free: (places, weight) => `Free: ${places} places, ${weight} kg`,
      noFreeStock: "No free stock: all cargo is already in orders or not received.",
    },
    deletionMark: {
      marked: "Marked for deletion",
      filterLabel: "Deletion mark",
      filterAll: "All",
      filterUnmarked: "Not marked",
      filterMarked: "Marked for deletion",
      mark: "Mark for deletion",
      unmark: "Clear deletion mark",
      markConfirmTitle: "Deletion mark",
      markConfirmMessage: (number) =>
        `Mark forwarding order ${number} for deletion? It becomes unposted and stays listed with the mark. Only an order without warehouse operations can be marked.`,
      unmarkConfirmMessage: (number) => `Clear the deletion mark of forwarding order ${number}? It is posted again.`,
      readOnly: "This forwarding order is marked for deletion: no new operations.",
    },
    transfer: {
      heading: "Transfer",
      description: "Move packages between warehouse locations.",
      occurredAt: "Date and time",
      submit: "Post transfer",
      submitting: "Posting...",
      success: "Transfer operation posted.",
      recent: "Recent transfer operations",
      empty: "No transfer operations yet.",
      total: "Total",
      confirmTitle: "Confirm transfer",
      confirmMessage: "Post this transfer operation?",
    },
    balances: {
      heading: "Stock balances",
      description: "Computed from the stock ledger for the selected receipt.",
      refresh: "Refresh",
      location: "Location",
      packageType: "Package type",
      balance: "Balance",
      weightBalance: "Weight (kg)",
      empty: "No stock movements yet for this forwarding order.",
      sfHeading: "SF",
      sfDescription:
        "Computed on the form: actual weight (from receiving) ÷ volume (package dimensions × available balance), kg/m³. Not editable.",
      sfWeight: "Weight, kg",
      sfVolume: "Volume, m³",
      sf: "SF, kg/m³",
      sfTotal: "Total",
      sfEmpty: "Needs actual weight and package dimensions to compute.",
    },
    connections: {
      heading: "Connections",
      description: "How this forwarding order's packages flow: receiving into locations, transfers between locations, and shipping/write-offs out.",
      legendReceived: "Received",
      legendTransferred: "Transferred",
      legendShipped: "Shipped / written off",
      legendReceiptTransfer: "Receipt transfer",
      receiptTransferOperations: "Receipt transfer operations",
      receiptTransferOperationLines: "Receipt transfer operation lines",
      sinkDeparture: "Departed",
      sinkWriteOff: "Written off",
      empty: "Add package types to this forwarding order to see its connections.",
      breakdown: {
        heading: "Full breakdown of all movements",
        date: "Date",
        type: "Type",
        packageType: "Package type",
        from: "From",
        to: "To",
        count: "Count",
        typeIn: "Receiving",
        typeOut: "Shipping / write-off",
        typeTransfer: "Transfer",
        receiptTransferOutPrefix: "Transferred to receipt",
        receiptTransferInPrefix: "Transferred from receipt",
        empty: "No movements posted for this forwarding order yet.",
      },
    },
    fileDrop: {
      hint: "Drop to attach files",
      unsupported: (names) => `Unsupported file type: ${names}`,
    },
    documents: {
      heading: "Files",
      description: "Photos and files attached to this forwarding order.",
      formats: "Supported formats: JPG, PNG, WEBP, HEIC, PDF, DOC, DOCX, XLS, XLSX.",
      addPhotos: "Add photos",
      addFiles: "Add files",
      uploading: "Uploading...",
      empty: "No files yet.",
      selectReceiptFirst: "Select a forwarding order above first.",
      download: "Open",
      delete: "Delete",
      deleteConfirmTitle: "Delete document",
      deleteConfirmMessage: "Delete this document? This cannot be undone.",
      storageDisabled: "File storage is not configured on the server.",
      tooLarge: "File is too large.",
      uploadedOn: "Uploaded",
      imageLoadError: "Image failed to load",
      saveFile: "Download",
      previewError: "Could not open the spreadsheet. Download the file to view it.",
      previewEmpty: "This sheet is empty.",
      previewTruncated: (shown: number, total: number) => `Showing the first ${shown} of ${total} rows.`,
    },
    chat: {
      heading: "Chat",
      description: "Discussion for this forwarding order.",
      receiptHeading: "Receipt chat ",
      selectReceiptFirst: "Select a forwarding order above first.",
      empty: "No messages yet. Start the conversation.",
      loading: "Loading messages...",
      placeholder: "Write a message...",
      send: "Send",
      sending: "Sending...",
      attachPhoto: "Attach photo",
      attachFile: "Attach file",
      systemBadge: "System",
      you: "You",
      unknownSender: "Unknown sender",
      imageLoadError: "Image failed to load",
      messageActions: "Message actions",
      copyMessage: "Copy message",
      replyTo: "Reply to message",
      replyingTo: "Replying to",
      cancelReply: "Cancel reply",
      deleteMessage: "Delete message",
      deleteConfirm: "Delete this message? This cannot be undone.",
      messageDeleted: "Message deleted",
      unreadDivider: "Unread messages",
      attachmentsPending: (count) =>
        `${count} file${count === 1 ? "" : "s"} ready to send`,
      addReaction: "Add reaction",
      readBy: "Read by",
      readReceiptsExpired: "Read receipts are kept for 3 days",
      messageSent: "Sent",
      messageRead: "Read",
      stickers: "Stickers",
      stickersLoading: "Loading stickers...",
      loadOlder: "Load earlier messages",
    },
    mainChat: {
      title: "All chats",
      openLabel: "Open chats",
      back: "All chats",
      loading: "Loading chats...",
      channelMain: "Main chat",
      channelDevelopers: "Chat with developers",
      channelOnecSync: "1C exchange errors",
      openSyncFailed: "Awaiting retry",
      sectionChannels: "Channels",
      sectionReceipts: "Forwarding orders",
      noMessagesYet: "No messages yet",
      openReceipt: "Open forwarding order",
      unreadCount: (count) => `Unread: ${count}`,
      documentTitle: (count) => `(${count}) Messages`,
      live: "Live",
      connecting: "Connecting…",
      offline: "Offline",
      loadMore: "Load older conversations",
    },
    backendUnavailable: "Warehouse backend is unavailable. Make sure it is running.",
  },
  pages: {
    back: "← Back",
    title: "All pages",
    subtitle: "Service pages of the app.",
    uiDescription: "Receiving, shipping and transfers by forwarding order.",
    dbDescription: "Edit database tables directly.",
    archDescription: "Every flow and business rule as diagrams.",
    syncFailedDescription: "Objects 1C rejected several times, waiting for a manual retry.",
    credentialsDescription: "A password and message for a new user.",
    syncQueueDescription: "What waits to be sent to 1C on the next exchange.",
    syncLogDescription: "The 1C exchange journal: what came in and went out, and when.",
    filesDescription: "Every file in S3 storage and from 1C: search, filters, sorting.",
    shippingDescription: "Open shipping orders of every forwarding order: pick a trip's orders and ship them with one button.",
  },
  tripShipping: {
    title: "Ship a trip",
    subtitle: "Open shipping orders of every forwarding order. Tick the trip's orders and ship them with one button: if any order is short of cargo, nothing is shipped.",
    back: "← All pages",
    refresh: "Refresh",
    search: "Order, forwarding order or cargo",
    empty: "No orders to ship.",
    noMatch: "Nothing found.",
    selectDay: (date: string) => `Select every order of ${date}`,
    places: "places",
    ordersCount: (count: number) => `${count} orders`,
    selectAll: "Select all",
    receipt: "Forwarding order",
    orderLabel: "order",
    noDimensions: "No dimensions",
    where: "Located",
    noStock: "Not in stock",
    viewByReceipt: "By forwarding order",
    viewByLocation: "By location",
    scopeSelected: (count: number) => `List for the selected orders: ${count}.`,
    scopeAll: (count: number) => `All open orders: ${count}. Tick the trip's orders in the other tab to list only them.`,
    listTitle: "Loading list",
    copyText: "Copy",
    copied: "Copied",
    print: "Print",
    volumeUnknown: "Volume not calculated: some package types have no dimensions.",
    placesN: (count: number) => `${count} ${count === 1 ? "place" : "places"}`,
    receiptMarked: "Forwarding order marked for deletion",
    readOnly: "Only storekeepers and administrators can ship.",
    selected: (count: number) => `Orders selected: ${count}`,
    clear: "Clear",
    ship: "Ship trip",
    confirmTitle: "Ship the trip?",
    confirmText: (count: number) => `Departures for ${count} orders will be posted with the current date. Stock is checked first: if any order is short of cargo, nothing is shipped.`,
    confirm: "Ship",
    checking: "Checking stock…",
    progress: (done: number, total: number) => `Shipped ${done} of ${total}…`,
    blockedTitle: "Trip not shipped",
    blockedText: "Nothing was shipped. Short of cargo:",
    shortage: (code: string, needPlaces: string, needKg: string, havePlaces: string, haveKg: string) =>
      `${code}: needs ${needPlaces} places (${needKg} kg), in stock ${havePlaces} places (${haveKg} kg)`,
    together: "together",
    doneTitle: "Trip shipped",
    doneText: (count: number) => `Departures posted: ${count}.`,
    stoppedTitle: "Shipping stopped",
    stoppedText: (order: string) => `Order ${order} was not shipped, shipping stopped:`,
    shipped: "Already shipped",
    left: "Not shipped",
    close: "Close",
  },
  files: {
    title: "Files",
    subtitle: "Chat photos, receipt documents and files from 1C. Search, filters and sorting run on the server.",
    search: "Search by name or receipt number",
    source: "Source",
    sourceAll: "All sources",
    sourceStorage: "S3 storage",
    sourceOnec: "1C",
    kind: "Type",
    kindAll: "All types",
    kinds: { image: "Images", pdf: "PDF", spreadsheet: "Spreadsheets", document: "Documents", other: "Other" },
    from: "From",
    to: "To",
    sort: "Sort",
    sorts: {
      "created_at:desc": "Newest first",
      "created_at:asc": "Oldest first",
      "name:asc": "Name A→Z",
      "name:desc": "Name Z→A",
      "size:desc": "Largest first",
      "size:asc": "Smallest first",
    },
    view: "View",
    viewGrid: "Grid",
    viewTable: "Table",
    reset: "Reset",
    name: "Name",
    receipt: "Receipt",
    size: "Size",
    created: "Added",
    open: "Open",
    empty: "No files found.",
    shown: (n: number, total: number) => `Showing ${n} of ${total}`,
    totalSize: (size: string) => `Total size: ${size}`,
    loadMore: "Load more",
    refresh: "Refresh",
    back: "← All pages",
  },
  credentials: {
    title: "Password generator",
    subtitle: "Generates a password, the message for the user and the bcrypt hash for users.password_hash. Computed in the browser.",
    login: "Login",
    host: "Address",
    password: "Password",
    regenerate: "Generate",
    message: "Message",
    copyMessage: "Copy message",
    hash: "bcrypt hash",
    hashing: "Hashing…",
    hashHint: "Put the hash into password_hash when creating the user.",
    copy: "Copy",
    copied: "Copied",
  },
  sync: {
    back: "← All pages",
    failedTitle: "1C: awaiting retry",
    failedSubtitle: "After three failed attempts an object is no longer served to 1C automatically. Fix the cause and press Retry.",
    queueTitle: "1C: exchange queue",
    queueSubtitle: "What 1C takes on its next exchange, in the order it takes it. An object leaves the queue once 1C acknowledges its revision.",
    emptyQueue: "The queue is empty — 1C has taken everything.",
    position: "#",
    revision: "Revision",
    logTitle: "1C: exchange log",
    logSubtitle: "Newest first. Kept for 90 days.",
    objectType: "Type",
    objectGuid: "GUID",
    errors: "Errors",
    error: "Error",
    enqueuedAt: "Queued since",
    retry: "Retry",
    retried: "Queued again",
    empty: "Nothing awaits a retry.",
    emptyLog: "No entries.",
    time: "Time",
    direction: "Direction",
    action: "Action",
    result: "Result",
    ok: "OK",
    failed: "Failed",
    inbound: "1C → App",
    outbound: "App → 1C",
    all: "All",
    searchGuid: "Search by GUID",
    loadMore: "Load more",
    loading: "Loading…",
    refresh: "Refresh",
    actions: { create: "Create", update: "Update", ack: "Acknowledge", fail: "1C failure" } as Record<string, string>,
    types: {
      forwarding_orders: "Forwarding order",
      expected_arrivals: "Expected arrival",
      counterparties: "Counterparty",
      warehouses: "Warehouse",
      files: "File",
    } as Record<string, string>,
    ackRemoved: (rev: number) => `Removed from the queue (revision ${rev}).`,
    ackStale: (sent: number, current: number) =>
      `Not removed: revision mismatch — 1C acknowledged ${sent}, the queue holds ${current}. Re-read the object and acknowledge revision ${current}, as served by the queue.`,
    ackNotQueued: (rev: number) =>
      `Not removed: the object is not queued (revision ${rev}) — acknowledged earlier, never queued, or wrong type / GUID.`,
    failCounted: (rev: number) => `1C could not write the object (revision ${rev}); the failure counts.`,
    failNotCounted: (rev: number) =>
      `The failure for revision ${rev} does not count: the object changed after it was read or is not queued.`,
    fieldsSent: "Fields from 1C",
    sentGuid: "GUID sent",
    shown: (shown: number, total: number) => `Showing ${shown} of ${total}`,
  },
  arch: {
    title: "Architecture",
    subtitle: "How the warehouse and the app work, in plain words, for someone seeing the project for the first time. Start with “How the warehouse works”; look up unfamiliar words in the glossary. Diagrams are in Russian.",
    renderError: "Could not render the diagram. Source:",
    viewer: {
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      fit: "Fit to view",
      fullscreen: "Full screen",
      close: "Close",
      hint: "Drag to pan, Ctrl + wheel or two fingers to zoom, double-click to zoom in",
      hintFullscreen: "Wheel or two fingers to zoom, drag to pan, Esc to close",
    },
    howToRead: {
      title: "How to read the diagrams",
      items: [
        "Rectangle — a step or a rule. Arrows show what comes next.",
        "Diamond — a question or a branch; the answers label the arrows leaving it.",
        "Cylinder — where data is stored.",
        "Dotted line — an explanation or a link, not the next step. A rectangle with no arrows is a standalone rule.",
        "Zoom in on a large diagram or open it full screen with the buttons above it.",
      ],
    },
    glossary: {
      title: "Glossary",
      description: "Terms used in the diagrams (in Russian).",
    },
    groups: {
      business: { title: "How the warehouse works", description: "Business processes: what happens to the cargo and by which rules. Read in order." },
      technical: { title: "How the app is built", description: "For developers and administrators: the parts of the system and how they talk to each other." },
    },
    sections: {
      overview: { title: "In short: the cargo’s journey", description: "The whole process in one diagram, from order to shipping. Start here." },
      lifecycle: { title: "Receipt statuses", description: "The states a cargo batch goes through and what moves it from one to the next." },
      receiving: { title: "Receiving", description: "What happens when cargo arrives, and how the system decides whether all of it or only part arrived." },
      operations: { title: "Shipping, write-off, transfer", description: "How cargo leaves the warehouse or changes location, and what the system checks first." },
      shipping: { title: "Shipping orders and departures", description: "How 1C forms a trip from shipping orders and how the storekeeper ships a whole order." },
      ledger: { title: "How stock is counted", description: "Stock is not stored as a number — it is computed from the log of ins and outs. Operations can’t be deleted." },
      fact: { title: "Cargo fact", description: "How much cargo there really is: while in the warehouse, what is there; after shipping, what left." },
      masterdata: { title: "Receipts, package types, reference data", description: "How numbers are issued, what can’t be deleted and where the plan comes from." },
      ui: { title: "The /ui screen", description: "What a user sees: landing tabs, the receipt card and its tabs." },
      reports: { title: "Lists and reports", description: "Where the figures on “In the warehouse”, “Expected”, the receipt card and search come from." },
      files: { title: "Files", description: "Photos and documents: uploaded in the app or sent from 1C." },
      chat: { title: "Chat", description: "A chat per receipt, shared channels and message rules." },
      dataflow: { title: "Data flow", description: "Where data comes from, how it is checked, where it is stored and who sees it. Each stream is its own coloured lane, read left to right; a «→ ③» flag means “continued in stream ③”, marked there «из ②» (“from ②”)." },
      system: { title: "System map", description: "The parts of the app and who talks to whom." },
      auth: { title: "Sign-in and permissions", description: "How signing in works, how long it lasts and what each role may do." },
      onec: { title: "1C exchange", description: "How the app and 1C exchange data: first 1C collects our changes, then sends its own." },
      onecqueue: { title: "Queue for 1C", description: "What happens to a changed receipt until 1C collects it, and what to do on errors." },
      realtime: { title: "Instant messages", description: "How chat and the “who’s online” list update without reloading the page." },
    },
  },
};

export default en;
